// Generation inspector — shows exactly what went INTO a generated ad:
// the raw video prompt sent to the model, the seed image + any burned-in
// text (the usual garbled-text culprit), the resolved titling script
// elements, and the static layout/image prompts.
// Fetches GET /api/ads/:id/generation-inspector.
// Clicking a seed/reference thumbnail opens the ORIGINAL catalog image
// (Media.fileUrl) next to the processed image that was actually submitted.
//
// Shared by ProductAds (AdDetailModal) and the legacy Ads page detail
// modal so both surfaces show the same generation inputs after a render.

import { useEffect, useRef, useState } from 'react';
import {
  Box, VStack, HStack, Text, Spinner, Button, Badge, Image, Textarea, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, Wrap, WrapItem, Code, Divider, Link, SimpleGrid
} from '@chakra-ui/react';
import { apiJson } from '../auth/apiFetch';

export type InspectorWarning = { code: string; message: string };

// The MASTER video ad this ad was derived from. Present only on derivatives —
// its absence means "this ad is a master (or not a derive at all)", never
// "lookup failed"; a failed lookup is `found: false` with a `reason`.
//
// WHY THIS EXISTS SEPARATELY FROM `video.derivedFrom`: that block is a
// URL-SHAPE HEURISTIC over THIS ad's own veoVideoUrl. It carries no ad id, so
// it cannot say WHICH ad the master is, and it is null whenever this ad has no
// video — i.e. it vanishes in exactly the case an operator most needs it, when
// the master failed and the derive inherited that failure.
export type InspectorMasterQc = {
  inspected?: boolean;
  passed?: boolean;
  skipped?: boolean;
  disabled?: boolean;
  reason?: string | null;
  summary?: string | null;
  // Last vision-QC attempt's own candidate. Thumbnail fallback only —
  // this panel is read-only and does NOT mount an override control
  // (overriding a MASTER's QC would revive the source plate every
  // derivative inherited; do that from the master's own AdDetailModal).
  discardedRenderUrl?: string | null;
};
export type InspectorMaster = {
  // The master's platformFormat — the stored linkage (Ad.deriveFromMaster is
  // this string, NOT an ad id; the master doc is resolved by sibling query).
  platformFormat: string;
  found: boolean;
  adId?: string | null;
  aspectRatio?: string | null;
  status?: string | null;
  previewUrl?: string | null;    // the master's DELIVERED titled video
  rawVideoUrl?: string | null;   // the master's raw pre-titling plate
  posterUrl?: string | null;
  thumbnailUrl?: string | null;
  predictionId?: string | null;  // the spend receipt
  visionQc?: InspectorMasterQc | null;
  failed?: boolean;
  failureMessage?: string | null;
  failureStage?: string | null;
  hasVideo?: boolean;
  reason?: string | null;        // set when found === false
};

export type InspectorData = {
  adId: string; kind: string; template: string; aspectRatio: string; status: string;
  generatedAt: string | null;
  warnings: InspectorWarning[];
  // Regenerate-with-prompt state, polled while a regen runs.
  metaSyncStatus?: string | null;
  regenerating?: boolean;
  regenerationStage?: string | null;
  lastRegeneration?: { status: 'pending' | 'done' | 'failed'; error: string | null; at: string | null } | null;
  seed?: { mediaId: string; source: string; fileType: string; url: string; burnedInText: string[]; seedHasText: boolean } | null;
  video?: {
    model: string | null; aspectRatio: string | null; prompt: string | null; storyboard: unknown;
    rawVideoUrl: string | null; finalUrl: string | null;
    derivedFrom?: { aspectRatio: '9:16'; videoUrl: string; thumbnailUrl: string | null } | null;
    // Submitted stack (pos 0 = seed). Each entry is either a legacy string
    // URL or a lineage object: `url` is what the model received (often a
    // pad/crop/reframe); `originalUrl` is the catalog Media.fileUrl before
    // that transform. Clicking a seed opens the original so an operator can
    // see whether expand/reframe altered the source.
    referenceImages?: Array<string | InspectorReferenceImage>;
    promptInheritedFromMaster?: boolean;
    referenceImagesInheritedFromMaster?: boolean;
    durationSec?: number | null;
    // Everything that rode alongside the prompt. Optional so payloads cached
    // before this shipped still render.
    submission?: InspectorSubmission | null;
  };
  master?: InspectorMaster | null;
  titling?: { engine?: string | null; format?: string | null; meta?: Record<string, unknown>; reconstructed?: boolean; error?: string };
  image?: {
    aiCanvasArtifactId: string | null; layoutInputArtifactId: string | null;
    model?: string | null;
    fontResolution?: {
      heading?: { requestedFamily: string | null; resolvedFamily: string | null; source: string | null; exact: boolean };
      body?: { requestedFamily: string | null; resolvedFamily: string | null; source: string | null; exact: boolean };
    } | null;
    // true when layoutPrompt is the EXACT prompt that produced the
    // current outputHtml (vs. a fallback from the legacy JSON generator,
    // which may not match — see the accompanying 'layout-prompt-legacy'
    // warning). Editing/regenerating works either way; this only affects
    // whether what's pre-filled reflects the actual render.
    promptIsExact?: boolean;
    layoutPrompt?: { system: string | null; user: string | null; images: string[] };
    canvasSpec?: unknown; outputHtml?: string | null; colorPalette?: unknown; copyPicks?: unknown;
    imageRefPrompt?: { prompt: string | null; seedSource: string | null; seedUrl: string | null };
    // Verbatim record of the image-model request, captured at submit time.
    // Absent means it was not recorded — never substituted with a guess.
    imageGeneration?: ImageGenerationRecord | null;
    // Which pipeline actually delivered this ad — read from the same capture
    // as imageGeneration, not inferred. 'direct_image' is the current path;
    // the retired 'direct_overlay' can still appear on ads rendered before
    // 2026-07-31. Null: no image-model submission was recorded at all (an
    // HTML-fallback render, or a render that predates this capture).
    pipeline?: string | null;
    // Which intent ran and what the data could support. See models/Ad.js.
    intentResolution?: {
      surface?: string | null; requested?: string | null; delivered?: string | null;
      fellBackFrom?: string | null; renderedRoles?: string[]; droppedRoles?: string[];
      generateSize?: string | null; logoComposited?: boolean;
    } | null;
    // Per-stage wall time for this render, in ms.
    renderStages?: { deriveMs?: number | null; renderMs?: number | null; uploadMs?: number | null } | null;
    // Submitted parameters for the image model — parity with video.submission.
    submission?: InspectorSubmission | null;
  };
};

export type ImageGenerationRecord = {
  // ⚠️ RECEIPT-ONLY RECORDS EXIST AND OMIT ALMOST EVERYTHING BELOW.
  // When a submit is CHARGED but never returns images — an Atlas quota
  // rejection, a 403, any failure upstream of the image coming back — the
  // backend stores a receipt so the money is not lost, and the inspector
  // sends exactly this and nothing more:
  //     { predictionId, model, submittedAt, receiptOnly: true }
  // No `images`, no `prompt`, no `size`, no `imageCount`.
  //
  // Everything except those four is therefore OPTIONAL AT RUNTIME regardless
  // of what this type says, and must be read defensively. Declaring `images`
  // as a required array is what let `images.length` ship unguarded and
  // white-screen the whole app on any pre-QC failure (see the note at the
  // render site below). A compile-time type cannot police a wire shape.
  receiptOnly?: boolean;
  provider?: string; model: string; predictionId: string | null; submittedAt: string;
  prompt?: string | null; size?: string | null; quality?: string | null;
  aspectRatio?: string | null; inputFidelity?: string | null;
  imageCount?: number;
  images?: { position: number; submittedUrl: string | null; sourceUrl: string | null; originalUrl?: string | null; role: string | null }[];
  pipeline?: string; stage?: string;
};

export type InspectorReferenceImage = {
  url: string;
  originalUrl?: string | null;
  mediaId?: string | null;
  method?: string | null;
  processed?: boolean;
  // WHAT THIS IMAGE ACTUALLY IS, not just where it lives. Ad.veoReferenceImages
  // is declared [String], so the submit record pins position and nothing else —
  // an operator could not tell the catalog primary from an alt from a
  // merchant-misfiled colourway photo. The backend now joins each URL back to
  // its Media row to answer that.
  position?: number;
  describes?: string | null;          // e.g. 'catalog alt (merchant feed image 1)'
  mediaSource?: string | null;
  imageRole?: string | null;
  feedIndex?: number | null;
  primarySubjectDesc?: string | null; // the merchant's own subject description
  // ⚠️ HONESTY FLAG: the descriptive fields above come from a LOOKUP performed
  // when the inspector was opened, not from the submit-time record. The URL LIST
  // is still verbatim and never re-derived, but a Media row deleted or
  // re-uploaded since the render resolves to nothing and is labelled
  // "not resolvable" rather than guessed at.
  resolvedFromUrl?: boolean;
};

// Submitted parameters that rode alongside the prompt. Shared shape across the
// video and static blocks so "everything the model received" means the same
// thing on both paths.
export type InspectorSubmission = {
  promptBytes?: number | null;
  // null is MEANINGFUL, not missing: no image model in use publishes a prompt
  // cap, so the static prompt is never truncated by one.
  promptByteCap?: number | null;
  promptBytesRemaining?: number | null;
  promptAtOrOverCap?: boolean | null;
  // Video only. 'raw-override' means buildVeoPrompt was BYPASSED.
  promptSource?: 'canonical-directives' | 'raw-override' | null;
  canonicalDirectivesBypassed?: boolean | null;
  model?: string | null;
  provider?: string | null;
  aspectRatio?: string | null;
  durationSec?: number | null;
  size?: string | null;
  quality?: string | null;
  inputFidelity?: string | null;
  referenceCount?: number | null;
  // Distinctness, not just count. A stack of 3 URLs that is really 2 images is
  // the quiet failure: it reads as complete while the model got less variety.
  distinctUrlCount?: number | null;
  distinctMediaCount?: number | null;
  distinctBasenameCount?: number | null;
  // ⚠️ None of the above is a pixel comparison — see the backend note. Silence
  // is not proof that every reference is a genuinely different view.
  distinctnessIsMetadataOnly?: boolean | null;
  predictionId?: string | null;
  operatorInputs?: {
    // FULL text, never truncated — a diagnostic that abbreviates the input is
    // the failure mode being fixed.
    videoPromptRaw?: string | null;
    videoPromptGuidance?: string | null;
    lastRefinementPrompt?: string | null;
    lastRefinementWasRawEdit?: boolean | null;
    lastRefinementAt?: string | null;
  } | null;
};

function normalizeRef(
  entry: string | InspectorReferenceImage,
  index: number,
  seedOriginal?: string | null
): InspectorReferenceImage {
  const url = typeof entry === 'string' ? entry : entry.url;
  const fromApi = typeof entry === 'string' ? null : (entry.originalUrl || null);
  // Pos 0 is the seed (models/Ad.js). If the API has not yet attached
  // lineage, seed.url is Media.fileUrl — the true catalog original.
  const originalUrl = fromApi || (index === 0 && seedOriginal ? seedOriginal : null);
  const method = typeof entry === 'string' ? null : (entry.method || null);
  // Pass the descriptive fields THROUGH. They were previously dropped here,
  // which is why a labelled backend payload still rendered as bare thumbnails.
  const obj = typeof entry === 'string' ? null : entry;
  return {
    url,
    originalUrl,
    mediaId: obj?.mediaId || null,
    method: method || (originalUrl && originalUrl !== url ? 'seed-media' : originalUrl === url ? 'unprocessed' : null),
    processed: !!(originalUrl && originalUrl !== url),
    position: obj?.position ?? index,
    describes: obj?.describes ?? null,
    mediaSource: obj?.mediaSource ?? null,
    imageRole: obj?.imageRole ?? null,
    feedIndex: obj?.feedIndex ?? null,
    primarySubjectDesc: obj?.primarySubjectDesc ?? null,
    resolvedFromUrl: obj?.resolvedFromUrl ?? false
  };
}

// Renders the submitted parameters as a compact definition grid. Values are
// shown verbatim; a missing value reads "not recorded" rather than being hidden,
// because a silently absent parameter is what made the original incident
// invisible.
function SubmissionBlock({ label, submission }: { label: string; submission: InspectorSubmission }) {
  const bytes = submission.promptBytes;
  const cap   = submission.promptByteCap;
  const overCap = !!submission.promptAtOrOverCap;
  const fmt = (n: number) => n.toLocaleString();
  const rows: Array<[string, React.ReactNode]> = [];

  if (submission.model !== undefined)    rows.push(['Model', submission.model || 'not recorded']);
  if (submission.provider)               rows.push(['Provider', submission.provider]);
  if (submission.aspectRatio !== undefined) rows.push(['Aspect ratio', submission.aspectRatio || 'not recorded']);
  if (submission.durationSec != null)    rows.push(['Duration', `${submission.durationSec}s`]);
  if (submission.size)                   rows.push(['Size', submission.size]);
  if (submission.quality)                rows.push(['Quality', submission.quality]);
  if (submission.inputFidelity)          rows.push(['Input fidelity', submission.inputFidelity]);

  if (bytes != null) {
    rows.push([
      'Prompt size',
      <HStack spacing={2} align="center" key="bytes">
        <Text fontSize="12px" color={overCap ? 'red.600' : undefined} fontWeight={overCap ? '700' : undefined}>
          {cap != null ? `${fmt(bytes)} / ${fmt(cap)} bytes` : `${fmt(bytes)} bytes`}
        </Text>
        {overCap && <Badge colorScheme="red" variant="solid" fontSize="0.55rem">AT CAP — TRUNCATED</Badge>}
        {/* null cap is a FACT, not a gap: no image model publishes one. */}
        {cap == null && <Text fontSize="11px" color="brand.muted">(this model publishes no prompt cap)</Text>}
      </HStack>
    ]);
  }

  if (submission.referenceCount != null) {
    const d = submission.distinctUrlCount;
    const m = submission.distinctMediaCount;
    const collapsed = d != null && d < submission.referenceCount;
    rows.push([
      'Reference images',
      <HStack spacing={2} align="center" key="refs">
        <Text fontSize="12px">
          {submission.referenceCount}
          {d != null && d !== submission.referenceCount ? ` submitted, ${d} distinct URL${d === 1 ? '' : 's'}` : ''}
          {m != null && d != null && m > 0 && m < d ? `, ${m} distinct source${m === 1 ? '' : 's'}` : ''}
        </Text>
        {collapsed && <Badge colorScheme="orange" variant="subtle" fontSize="0.55rem">duplicates</Badge>}
      </HStack>
    ]);
  }

  if (submission.predictionId !== undefined) {
    rows.push(['Prediction id', submission.predictionId || 'not recorded']);
  }

  if (!rows.length) return null;

  return (
    <Box mb={3}>
      <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1} textTransform="uppercase">{label}</Text>
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <VStack align="stretch" spacing={1}>
          {rows.map(([k, v]) => (
            <HStack key={k} align="flex-start" spacing={3}>
              <Text fontSize="11px" color="brand.muted" minW="110px">{k}</Text>
              {typeof v === 'string'
                ? <Text fontSize="12px" wordBreak="break-all">{v}</Text>
                : v}
            </HStack>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

function imageStageLabel(stage: string | null | undefined): string {
  switch (stage) {
    case 'pending':   return 'Queued…';
    case 'image-gen': return 'Regenerating layout…';
    case 'image-ref': return 'Polishing image…';
    case 'done':      return 'Done';
    case 'failed':    return 'Failed';
    default:          return 'Regenerating…';
  }
}

function PromptBlock({
  label, text, emptyHint, inherited
}: {
  label: string;
  text: string | null | undefined;
  emptyHint?: string;
  inherited?: boolean;
}) {
  const toast = useToast();
  if (!text) {
    if (!emptyHint) return null;
    return (
      <Box p={3} borderWidth="1px" borderColor="orange.200" bg="orange.50" borderRadius="md">
        <Text fontSize="11px" fontWeight="700" color="orange.800" mb={1} textTransform="uppercase">{label}</Text>
        <Text fontSize="12px" color="orange.800">{emptyHint}</Text>
      </Box>
    );
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Prompt copied', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Copy failed', status: 'error', duration: 3000 });
    }
  };
  return (
    <Box>
      <HStack mb={1} justify="space-between" align="center">
        <HStack spacing={2}>
          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">{label}</Text>
          {inherited && (
            <Badge colorScheme="purple" variant="subtle" fontSize="0.55rem">from master</Badge>
          )}
        </HStack>
        <Button size="xs" variant="ghost" onClick={() => void copy()}>Copy</Button>
      </HStack>
      <Code display="block" whiteSpace="pre-wrap" fontSize="12px" p={3} borderRadius="md"
        maxH="320px" overflowY="auto" w="100%">{text}</Code>
    </Box>
  );
}

type CompareTarget = {
  sentUrl: string;
  originalUrl: string | null;
  label: string;
  method: string | null;
  processed: boolean;
};

function SeedCompareModal({ target, onClose }: { target: CompareTarget; onClose: () => void }) {
  const differs = !!(target.originalUrl && target.originalUrl !== target.sentUrl);
  return (
    <Modal isOpen={true} onClose={onClose} size={differs ? '6xl' : 'xl'} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="700">
            {differs ? 'Original catalog vs sent to the model' : 'Seed image'}
          </Text>
          <Text fontSize="12px" color="brand.muted" fontWeight="400" mt={1}>
            {differs
              ? 'The left image is the raw catalog source. The right image is what was actually submitted after pad/crop/reframe/outpaint.'
              : target.originalUrl
                ? 'This catalog original was sent to the model as-is — no reframe/pad was applied.'
                : 'No original catalog media could be traced for this reference. Showing the image that was sent.'}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {differs ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <HStack mb={2} spacing={2}>
                  <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                    Original catalog
                  </Text>
                  <Badge variant="outline" fontSize="0.55rem">pre-processing</Badge>
                </HStack>
                <Image src={target.originalUrl!} alt="original catalog" w="100%" borderRadius="md"
                  borderWidth="1px" borderColor="brand.border" />
              </Box>
              <Box>
                <HStack mb={2} spacing={2}>
                  <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                    Sent to the model
                  </Text>
                  {target.method && <Badge colorScheme="orange" fontSize="0.55rem">{target.method}</Badge>}
                </HStack>
                <Image src={target.sentUrl} alt="sent to model" w="100%" borderRadius="md"
                  borderWidth="1px" borderColor="brand.border" />
              </Box>
            </SimpleGrid>
          ) : (
            <Image src={target.originalUrl || target.sentUrl} alt={target.label} w="100%" borderRadius="md"
              borderWidth="1px" borderColor="brand.border" />
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ClickableSeedThumb({
  src, alt, badge, processed, onOpen
}: {
  src: string;
  alt: string;
  badge: string;
  processed: boolean;
  onOpen: () => void;
}) {
  return (
    <Box as="button" type="button" onClick={onOpen} title="Compare with original catalog image"
      cursor="pointer" textAlign="left" bg="transparent" p={0} border="none" lineHeight={0}>
      <Box position="relative">
        <Image src={src} alt={alt} boxSize="88px" objectFit="cover"
          borderRadius="md" borderWidth="1px" borderColor="brand.border" />
        <Badge position="absolute" bottom="2px" left="2px" fontSize="0.55rem"
          colorScheme={badge === 'seed' ? 'orange' : 'gray'}>
          {badge}
        </Badge>
        {processed && (
          <Badge position="absolute" top="2px" right="2px" fontSize="0.5rem" colorScheme="purple">
            reframed
          </Badge>
        )}
      </Box>
    </Box>
  );
}

function nonEmptyUrl(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * "Master video ad" — the source ad a derivative was cropped/retitled from.
 *
 * ⚠️ THE FAILED MASTER IS THE PRIMARY CASE, NOT AN EDGE CASE. This panel exists
 * to QC a master whose output was WRONG, so `failed` is a STATUS TREATMENT and
 * is deliberately NOT allowed to suppress the preview links. That distinction is
 * load-bearing: on a titling failure the backend flips the master to `failed`
 * with "master rendered; titling failed" and KEEPS the paid raw plate, so a
 * failed master very often still has a perfectly inspectable video — hiding the
 * link there would break the link in precisely the case it is needed. Links are
 * therefore gated on a URL actually being present, never on `failed`.
 *
 * Read-only by construction: no fetch, no adId re-pointing. The parent's `adId`
 * prop also drives a BILLABLE regenerate POST, so this panel must never change
 * what that prop refers to.
 */
function MasterVideoAdPanel({ master, thisAspect }: { master: InspectorMaster; thisAspect?: string | null }) {
  const missing     = master.found === false;
  const failed      = master.failed === true;
  const previewUrl  = nonEmptyUrl(master.previewUrl)  ? master.previewUrl  : null;
  const rawVideoUrl = nonEmptyUrl(master.rawVideoUrl) ? master.rawVideoUrl : null;
  const rawDiscarded = master.visionQc?.discardedRenderUrl;
  const discardedUrl = nonEmptyUrl(rawDiscarded) ? rawDiscarded : null;
  const thumbUrl    = nonEmptyUrl(master.thumbnailUrl)
    ? master.thumbnailUrl
    : discardedUrl;
  // Two distinct URLs are worth two links; one URL serving both roles is one.
  const showRaw     = !!rawVideoUrl && rawVideoUrl !== previewUrl;
  const anyLink     = !!previewUrl || !!rawVideoUrl;
  // discardedRenderUrl can be the only remaining candidate on a QC-failed
  // master whose delivered preview/raw URLs were cleared. Show that
  // thumbnail even when the titled/raw links are absent.
  const anyPreview  = anyLink || !!thumbUrl;

  const tone        = missing ? 'orange' : failed ? 'red' : null;
  const borderColor = tone ? `${tone}.300` : 'brand.border';
  const bg          = tone ? `${tone}.50`  : 'brand.surface';

  const qc = master.visionQc || null;
  // Only claim a QC verdict when one was actually reached.
  const qcInspected = !!qc && qc.inspected !== false && !qc.skipped && !qc.disabled;

  const rows: Array<[string, string]> = [];
  if (!missing) {
    rows.push(['Master ad id', master.adId || 'not recorded']);
    if (master.status)       rows.push(['Master status', master.status]);
    if (master.aspectRatio)  rows.push(['Master aspect', master.aspectRatio]);
    rows.push(['Prediction id', master.predictionId || 'not recorded']);
  }

  return (
    <Box mb={3} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={bg}>
      <HStack mb={2} spacing={2} flexWrap="wrap">
        <Text fontSize="11px" fontWeight="700" color={failed ? 'red.700' : 'brand.muted'} textTransform="uppercase">
          Master video ad — the source, not this ad
        </Text>
        {master.platformFormat && <Badge variant="outline" fontSize="0.55rem">{master.platformFormat}</Badge>}
        {master.status && (
          <Badge fontSize="0.55rem" colorScheme={failed ? 'red' : 'gray'}>{master.status}</Badge>
        )}
        {missing && <Badge colorScheme="orange" variant="solid" fontSize="0.55rem">NOT FOUND</Badge>}
        {failed  && <Badge colorScheme="red"    variant="solid" fontSize="0.55rem">MASTER FAILED</Badge>}
        {qcInspected && (
          <Badge colorScheme={qc?.passed ? 'green' : 'red'} variant="subtle" fontSize="0.55rem">
            master QC {qc?.passed ? 'passed' : 'FAILED'}
          </Badge>
        )}
      </HStack>

      <Text fontSize="12px" mb={2}>
        {thisAspect ? `This ${thisAspect} ad` : 'This ad'} is a derivative of the{' '}
        <Text as="span" fontWeight="700">{master.platformFormat || 'master'}</Text> master
        {missing
          ? ' — which could not be located. It has most likely been deleted or archived, so there is no source video to open.'
          : failed
            ? ' — which FAILED. This derivative inherited that outcome, so diagnose the master, not this ad.'
            : '. Defects visible here usually originate in the master below.'}
      </Text>

      {missing && nonEmptyUrl(master.reason) && (
        <Text fontSize="12px" color="orange.800" mb={2} whiteSpace="pre-wrap" wordBreak="break-word">
          {master.reason}
        </Text>
      )}

      {/* Verbatim failure text — never truncated; this IS the diagnostic. */}
      {failed && (nonEmptyUrl(master.failureStage) || nonEmptyUrl(master.failureMessage)) && (
        <Box mb={2}>
          {nonEmptyUrl(master.failureStage) && (
            <HStack align="flex-start" spacing={3} mb={1}>
              <Text fontSize="11px" color="red.700" minW="110px" flexShrink={0}>Failure stage</Text>
              <Text fontSize="12px" whiteSpace="pre-wrap" wordBreak="break-word">{master.failureStage}</Text>
            </HStack>
          )}
          {nonEmptyUrl(master.failureMessage) && (
            <HStack align="flex-start" spacing={3}>
              <Text fontSize="11px" color="red.700" minW="110px" flexShrink={0}>Failure message</Text>
              <Text fontSize="12px" whiteSpace="pre-wrap" wordBreak="break-word">{master.failureMessage}</Text>
            </HStack>
          )}
        </Box>
      )}

      {qcInspected && nonEmptyUrl(qc?.summary) && (
        <HStack align="flex-start" spacing={3} mb={2}>
          <Text fontSize="11px" color="brand.muted" minW="110px" flexShrink={0}>Master QC</Text>
          <Text fontSize="12px" whiteSpace="pre-wrap" wordBreak="break-word">{qc?.summary}</Text>
        </HStack>
      )}

      {/* Preview links. Gated on a real URL — NOT on `failed`. See the header
          comment: a failed master usually still holds its paid plate.
          anyPreview also covers a discardedRenderUrl-only thumbnail. */}
      {anyPreview && (
        <HStack align="center" spacing={3} mb={2}>
          {thumbUrl && (
            <Image
              src={thumbUrl}
              alt={`${master.platformFormat || 'master'} video`}
              boxSize="88px"
              objectFit="cover"
              borderRadius="md"
              flexShrink={0}
            />
          )}
          <VStack align="start" spacing={0.5}>
            {previewUrl && (
              <Link href={previewUrl} isExternal color="rsViolet.600" fontSize="12px" fontWeight="700">
                Open master video preview ↗
              </Link>
            )}
            {showRaw && rawVideoUrl && (
              <Link href={rawVideoUrl} isExternal color="rsViolet.600" fontSize="12px">
                raw master plate (pre-titling) ↗
              </Link>
            )}
          </VStack>
        </HStack>
      )}

      {/* No URL at all. Say why rather than rendering a dead link. */}
      {!missing && !anyPreview && (
        <Text fontSize="12px" color={failed ? 'red.800' : 'orange.800'} mb={2}>
          The master produced no video plate, so there is nothing to preview — and that is
          why this derivative has no output. Take the ids below to the render logs.
        </Text>
      )}

      {rows.length > 0 && (
        <VStack align="stretch" spacing={1}>
          {rows.map(([label, value]) => (
            <HStack key={label} align="flex-start" spacing={3}>
              <Text fontSize="11px" color="brand.muted" minW="110px" flexShrink={0}>{label}</Text>
              <Code fontSize="12px" userSelect="all" wordBreak="break-all">{value}</Code>
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
}

export function GenerationInspectorModal({ adId: initialAdId, brandId, onClose }: { adId: string; brandId: string; onClose: () => void }) {
  const toast = useToast();
  // The ad currently being inspected. Starts at the ad the caller opened the
  // modal for; "View master's generation details" pivots this to the
  // master's real _id (from `data.master.adId`, resolved server-side — see
  // GenerationInspectorModal's `InspectorMaster` type) so the SAME modal
  // instance re-fetches and shows the master's inputs, without the caller
  // needing to know the pivot happened. `originAdId` is fixed at mount so a
  // "back" control can always return to where the operator started.
  const [adId, setAdId] = useState(initialAdId);
  const [originAdId] = useState(initialAdId);
  const [data, setData] = useState<InspectorData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Mirrors `adId` for the regenerate poll below (started at click-time by
  // `postRegenerate`, so its closures cannot see a LATER pivot the way the
  // `[adId, brandId]` fetch effect's own cleanup already does). A ref, not
  // just reading `adId` directly, because it must reflect the CURRENT
  // render's value inside a callback created by an EARLIER render.
  const currentAdIdRef = useRef(adId);
  currentAdIdRef.current = adId;

  // Pivot the modal to inspect a different ad (currently only used to jump
  // to a derivative's master). Resets every piece of per-ad UI state so the
  // new fetch renders like a fresh mount rather than showing a stale editor,
  // compare-view, or in-flight regenerate poll left over from the ad being
  // navigated away from.
  const viewAd = (targetAdId: string) => {
    if (!targetAdId || targetAdId === adId) return;
    setEditingPrompt(false);
    setEditingImagePrompt(false);
    setEditSystem('');
    setEditUser('');
    setEditImageRaw('');
    setRegenError(null);
    setRegenSubmitting(false);
    setCompare(null);
    setError(null);
    setData(null);
    setLoading(true);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setAdId(targetAdId);
  };

  const copyAdId = async () => {
    try {
      await navigator.clipboard.writeText(adId);
      toast({ title: 'Ad ID copied', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Copy failed', description: adId, status: 'error', duration: 4000 });
    }
  };

  // Edit-the-exact-prompt + regenerate (image ads only).
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editSystem, setEditSystem] = useState('');
  const [editUser, setEditUser] = useState('');
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Edit the IMAGE-MODEL prompt — the one rendered read-only just below the
  // reference thumbnails. Distinct from the pair above, which edits the LAYOUT
  // LLM's {system,user} and only applies to legacy canvas ads.
  const [editingImagePrompt, setEditingImagePrompt] = useState(false);
  const [editImageRaw, setEditImageRaw] = useState('');
  const [compare, setCompare] = useState<CompareTarget | null>(null);

  const fetchInspector = async () => {
    const r = await apiJson<{ inspector: InspectorData }>(
      `/api/ads/${encodeURIComponent(adId)}/generation-inspector?brandId=${encodeURIComponent(brandId)}`
    );
    return r.inspector;
  };

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const inspector = await fetchInspector();
        if (!stopped) setData(inspector);
      } catch (err) {
        if (!stopped) setError((err as Error).message);
      } finally {
        if (!stopped) setLoading(false);
      }
    })();
    return () => {
      stopped = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId, brandId]);

  const startEditingPrompt = () => {
    setEditSystem(data?.image?.layoutPrompt?.system || '');
    setEditUser(data?.image?.layoutPrompt?.user || '');
    setRegenError(null);
    setEditingPrompt(true);
  };

  // ONE regenerate POST + poll, shared by both editors. MONEY: every call here
  // is one billable image submit, so the two editors must not each grow their
  // own copy of this and drift apart.
  const postRegenerate = async (body: Record<string, unknown>, closeEditor: () => void) => {
    // The ad this specific submission targets — fixed at call time. Neither
    // regenerate editor is reachable from the master panel today (that
    // panel only appears for a VIDEO derivative's `data.master`, while
    // regenerate is image-only), so `viewAd`'s own interval-clear already
    // covers the reachable case; `currentAdIdRef` below is defense in depth
    // for a POST or poll tick that resolves in the small window AFTER a
    // pivot but BEFORE `viewAd`'s cleanup runs, so a stale result for THIS
    // ad can never be written into what may by then be a different ad's data.
    const forAdId = adId;
    setRegenSubmitting(true);
    setRegenError(null);
    try {
      await apiJson(`/api/ads/${encodeURIComponent(forAdId)}/regenerate?brandId=${encodeURIComponent(brandId)}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body)
      });
      closeEditor();
      if (currentAdIdRef.current !== forAdId) return;
      setData(prev => prev ? { ...prev, regenerating: true, regenerationStage: 'pending' } : prev);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        if (currentAdIdRef.current !== forAdId) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          return;
        }
        try {
          const fresh = await fetchInspector();
          if (currentAdIdRef.current !== forAdId) return;
          setData(fresh);
          if (!fresh.regenerating && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // regenerating just flips to false whether the run succeeded or
            // failed — check the outcome explicitly so a server-side failure
            // doesn't silently vanish behind a spinner that just stops.
            if (fresh.lastRegeneration?.status === 'failed') {
              setRegenError(fresh.lastRegeneration.error || 'Regeneration failed.');
            }
          }
        } catch {
          // Transient poll failure — keep trying on the next tick.
        }
      }, 4000);
    } catch (err) {
      if (currentAdIdRef.current === forAdId) setRegenError((err as Error).message);
    } finally {
      if (currentAdIdRef.current === forAdId) setRegenSubmitting(false);
    }
  };

  // Legacy canvas path — edits the LAYOUT LLM prompt, not the image model's.
  const submitPromptRegenerate = async () => {
    const system = editSystem.trim();
    const user = editUser.trim();
    if (!system || !user) return;
    await postRegenerate({ promptOverride: { system, user } }, () => setEditingPrompt(false));
  };

  // Prefill with the verbatim string the image model was sent for the render
  // on screen — captured at submit time, never re-derived.
  const startEditingImagePrompt = () => {
    setEditImageRaw(data?.image?.imageGeneration?.prompt || '');
    setRegenError(null);
    setEditingImagePrompt(true);
  };

  // Full replacement of the static prompt. Sends imagePromptRaw, NOT
  // promptOverride: the two are mutually exclusive server-side (both land in
  // the same slot) and promptOverride requires a {system,user} pair the image
  // model has no channel for.
  const submitImagePromptRegenerate = async () => {
    const raw = editImageRaw.trim();
    if (!raw) return;
    await postRegenerate({ imagePromptRaw: raw }, () => setEditingImagePrompt(false));
  };

  const titlingMeta = data?.titling?.meta || {};
  const metaRows = Object.entries(titlingMeta).filter(([, v]) => v != null && v !== '' && typeof v !== 'object');
  const isExported = data?.metaSyncStatus === 'synced';
  const isRegenerating = !!data?.regenerating;
  const generatedTimestamp = (() => {
    if (!data?.generatedAt) return null;
    const date = new Date(data.generatedAt);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
  })();

  return (
    <>
    <Modal isOpen={true} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2} align="center">
            <Text fontSize="lg" fontWeight="700">Generation details</Text>
            {/* At-a-glance: the ad being inspected is not itself a master. */}
            {data?.master && (
              <Badge colorScheme="purple" variant="subtle" fontSize="0.6rem">derivative</Badge>
            )}
            {/* Only reachable after "View master's generation details" — a
                master itself is never opened with a mismatched originAdId. */}
            {adId !== originAdId && (
              <Button size="xs" variant="link" colorScheme="purple" onClick={() => viewAd(originAdId)}>
                ← back to derivative's generation details
              </Button>
            )}
          </HStack>
          <HStack spacing={2} mt={1}>
            <Text fontSize="11px" color="brand.muted" fontFamily="mono">{adId}</Text>
            <Button size="xs" variant="ghost" onClick={() => void copyAdId()}>Copy ID</Button>
          </HStack>
          {generatedTimestamp && (
            <Text fontSize="11px" color="brand.muted" mt={1}>
              Generated {generatedTimestamp}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <HStack py={8} justify="center"><Spinner /><Text>Loading generation inputs…</Text></HStack>
          ) : error ? (
            <Text color="red.500">{error}</Text>
          ) : data ? (
            <VStack align="stretch" spacing={4}>
              {/* Warnings — the garble diagnosis lives here */}
              {data.warnings?.map((w, i) => (
                <Box key={i} bg={w.code === 'seed-has-burned-in-text' ? 'orange.50' : 'gray.50'}
                  borderWidth="1px" borderColor={w.code === 'seed-has-burned-in-text' ? 'orange.300' : 'brand.border'}
                  borderRadius="md" p={3}>
                  <Text fontSize="13px" color={w.code === 'seed-has-burned-in-text' ? 'orange.800' : 'brand.ink'}>
                    ⚠️ {w.message}
                  </Text>
                </Box>
              ))}

              {/* Seed image + burned-in text */}
              {data.seed && (
                <Box>
                  <Text fontSize="12px" fontWeight="700" mb={2}>Seed image ({data.seed.source})</Text>
                  <HStack align="start" spacing={3}>
                    {data.seed.url && (
                      <Box as="button" type="button" onClick={() => setCompare({
                        sentUrl: data.seed!.url,
                        originalUrl: data.seed!.url,
                        label: 'seed',
                        method: null,
                        processed: false
                      })} title="Open original catalog image" cursor="pointer"
                        bg="transparent" p={0} border="none" lineHeight={0}>
                        <Image src={data.seed.url} alt="seed" maxW="160px" borderRadius="md" />
                      </Box>
                    )}
                    <Box>
                      {data.seed.seedHasText ? (
                        <>
                          <Badge colorScheme="orange" mb={1}>burned-in text detected</Badge>
                          <VStack align="start" spacing={0.5}>
                            {data.seed.burnedInText.map((t, i) => (
                              <Text key={i} fontSize="12px" fontFamily="mono">“{t}”</Text>
                            ))}
                          </VStack>
                        </>
                      ) : (
                        <Badge colorScheme="green">no burned-in text detected</Badge>
                      )}
                    </Box>
                  </HStack>
                </Box>
              )}

              {/* Master video ad (derivatives only).
                  DELIBERATELY ITS OWN SECTION, not nested inside the `data.video`
                  block below: a derivative whose master failed can have no video
                  block at all, and that is the case this panel exists for. Nesting
                  it would hide it exactly when it matters. */}
              {data.master && (
                <>
                  <Divider />
                  <MasterVideoAdPanel master={data.master} thisAspect={data.aspectRatio} />
                  {/* Pivot the SAME modal to the master's own generation
                      details. Lives here, not inside MasterVideoAdPanel,
                      because that panel is documented read-only ("no adId
                      re-pointing") — this is the pivot, kept in the parent
                      that owns `adId` state. Gated on `found` + a real
                      `adId`: a not-found master has neither. */}
                  {data.master.found && data.master.adId && (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      alignSelf="flex-start"
                      onClick={() => viewAd(data.master!.adId!)}
                    >
                      View master's generation details →
                    </Button>
                  )}
                </>
              )}

              {/* Video */}
              {data.video && (
                <>
                  <Divider />
                  <Box>
                    <HStack mb={2} spacing={2}>
                      <Text fontSize="12px" fontWeight="700">Video generation</Text>
                      {data.video.model && <Badge>{data.video.model}</Badge>}
                      {data.video.aspectRatio && <Badge variant="outline">{data.video.aspectRatio}</Badge>}
                    </HStack>
                    <HStack spacing={4} mb={3} fontSize="12px">
                      {data.video.rawVideoUrl && (
                        <Link href={data.video.rawVideoUrl} isExternal color="rsViolet.600">
                          raw model output (pre-titling) ↗
                        </Link>
                      )}
                      {data.video.finalUrl && (
                        <Link href={data.video.finalUrl} isExternal color="rsViolet.600">
                          final (with titling) ↗
                        </Link>
                      )}
                    </HStack>
                    {data.video.derivedFrom && (
                      <HStack
                        align="center"
                        spacing={3}
                        mb={3}
                        p={3}
                        borderWidth="1px"
                        borderColor="brand.border"
                        borderRadius="md"
                        bg="brand.surface"
                      >
                        <Link href={data.video.derivedFrom.videoUrl} isExternal flexShrink={0}>
                          {data.video.derivedFrom.thumbnailUrl && (
                            <Image
                              src={data.video.derivedFrom.thumbnailUrl}
                              alt="9:16 source video"
                              boxSize="88px"
                              objectFit="cover"
                              borderRadius="md"
                            />
                          )}
                        </Link>
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                            Crop source
                          </Text>
                          <Text fontSize="12px" mb={1}>
                            This {data.aspectRatio} video was derived from a {data.video.derivedFrom.aspectRatio} master.
                          </Text>
                          <Link href={data.video.derivedFrom.videoUrl} isExternal color="rsViolet.600" fontSize="12px">
                            Open source video in Cloudinary ↗
                          </Link>
                        </Box>
                      </HStack>
                    )}
                    <Text fontSize="11px" color="brand.muted" mb={2}>
                      Tip: if garbled text is in the raw output, it came from the video model (usually a text-bearing seed image above), not titling.
                    </Text>

                    {/* THE RAW-OVERRIDE ALARM. When set, buildVeoPrompt is
                        bypassed entirely, so the camera directives and the
                        product-fidelity block are NOT in the payload — and the
                        prompt below is operator text, not an assembled prompt.
                        Without this, a short raw prompt is indistinguishable
                        from an ordinary one and an operator cannot tell "the
                        directives ran and failed" from "they never ran". */}
                    {data.video.submission?.canonicalDirectivesBypassed && (
                      <Box mb={3} p={3} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md">
                        <HStack spacing={2} mb={1}>
                          <Badge colorScheme="red" variant="solid" fontSize="0.55rem">RAW OVERRIDE</Badge>
                          <Text fontSize="sm" fontWeight="700" color="red.700">
                            Canonical directives were NOT sent
                          </Text>
                        </HStack>
                        <Text fontSize="11px" color="red.800">
                          A raw camera prompt replaced the whole assembled prompt for this render, so the
                          canonical camera directives, the product-fidelity block and the timeline were
                          never submitted. The text below is the operator&apos;s own prompt (byte-capped) —
                          judge the render against that alone, not against the standard pipeline.
                        </Text>
                      </Box>
                    )}

                    <PromptBlock
                      label={data.video.submission?.canonicalDirectivesBypassed
                        ? 'Exact prompt sent to the video model (RAW — replaces the assembled prompt)'
                        : 'Exact prompt sent to the video model'}
                      text={data.video.prompt}
                      inherited={!!data.video.promptInheritedFromMaster}
                      emptyHint="No prompt is stored for this ad. Either no video model ran (crop/retitle, UGC passthrough, or video-seed reuse), the render predates prompt persistence, or the submit failed before the prompt was stamped."
                    />
                    <Box h={3} />

                    {/* Submitted parameters — model, aspect, duration, prompt
                        bytes against the model's own cap, reference count and
                        distinctness. Previously none of this was on any API
                        surface. */}
                    {data.video.submission && (
                      <SubmissionBlock label="Submitted to the video model" submission={data.video.submission} />
                    )}

                    {/* OPERATOR INPUTS, in full. These are the fields that could
                        inject text into a generation with nothing showing it:
                        videoPromptRaw appeared on NO API response before this. */}
                    {data.video.submission?.operatorInputs && (
                      <>
                        <PromptBlock
                          label="Operator raw camera prompt (videoPromptRaw) — replaces the whole prompt"
                          text={data.video.submission.operatorInputs.videoPromptRaw}
                        />
                        {data.video.submission.operatorInputs.videoPromptRaw && <Box h={3} />}
                        <PromptBlock
                          label="Operator guidance (videoPromptGuidance) — prepended as refinement"
                          text={data.video.submission.operatorInputs.videoPromptGuidance}
                        />
                        {data.video.submission.operatorInputs.videoPromptGuidance && <Box h={3} />}
                        {/* The last regenerate's refinement note. A null note
                            with rawPromptEdit true is honest: a verbatim
                            override ran and its text is not retained on the Ad
                            (regenerate overrides are pass-through by design). */}
                        {(data.video.submission.operatorInputs.lastRefinementPrompt
                          || data.video.submission.operatorInputs.lastRefinementWasRawEdit) && (
                          <Box mb={3}>
                            <PromptBlock
                              label="Last regenerate — operator refinement note"
                              text={data.video.submission.operatorInputs.lastRefinementPrompt}
                              emptyHint={data.video.submission.operatorInputs.lastRefinementWasRawEdit
                                ? 'The last regenerate used a verbatim prompt override. Its text is not stored on the ad — regenerate overrides are pass-through by design, so only the fact that one ran is recorded.'
                                : undefined}
                            />
                          </Box>
                        )}
                        {/* A plain re-render is a real, deliberate state — say
                            so instead of leaving an empty section that reads
                            like missing data. */}
                        {!data.video.submission.operatorInputs.videoPromptRaw
                          && !data.video.submission.operatorInputs.videoPromptGuidance
                          && !data.video.submission.operatorInputs.lastRefinementPrompt
                          && !data.video.submission.operatorInputs.lastRefinementWasRawEdit && (
                          <Box mb={3} p={2} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                            <Text fontSize="11px" color="brand.muted">
                              No operator input on this render — the prompt above is the untouched
                              pipeline output.
                            </Text>
                          </Box>
                        )}
                      </>
                    )}

                    {/* Exactly the stack submitted at render time — never re-derived */}
                    {data.video.referenceImages && data.video.referenceImages.length > 0 && (
                      <Box mb={3}>
                        <HStack mb={1} spacing={2}>
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                            Images sent to the video model
                          </Text>
                          {data.video.referenceImagesInheritedFromMaster && (
                            <Badge colorScheme="purple" variant="subtle" fontSize="0.55rem">from master</Badge>
                          )}
                        </HStack>
                        <Text fontSize="11px" color="brand.muted" mb={2}>
                          Thumbnails are what the model received. Click a seed to open the original catalog image (before any pad/crop/reframe).
                        </Text>
                        <Wrap spacing={2}>
                          {data.video.referenceImages.map((raw, i) => {
                            const ref = normalizeRef(raw, i, data.seed?.url);
                            return (
                              <WrapItem key={ref.url + i}>
                                {/* WHAT each image is, under the thumbnail. A
                                    bare grid of thumbnails cannot tell the
                                    catalog primary from an alt from a
                                    misfiled colourway photo — that ambiguity
                                    is what let a wrong reference drive a
                                    billable generation unnoticed. */}
                                <Box maxW="120px">
                                  <ClickableSeedThumb
                                    src={ref.url}
                                    alt={i === 0 ? 'seed' : `reference ${i}`}
                                    badge={i === 0 ? 'seed' : `#${i}`}
                                    processed={!!ref.processed}
                                    onOpen={() => setCompare({
                                      sentUrl: ref.url,
                                      originalUrl: ref.originalUrl || null,
                                      label: i === 0 ? 'seed' : `reference ${i}`,
                                      method: ref.method || null,
                                      processed: !!ref.processed
                                    })}
                                  />
                                  {ref.describes && (
                                    <Text fontSize="10px" color="brand.muted" mt={1} lineHeight="1.25">
                                      {ref.describes}
                                    </Text>
                                  )}
                                  {ref.primarySubjectDesc && (
                                    <Text fontSize="10px" color="brand.muted" mt={0.5} fontStyle="italic" lineHeight="1.25">
                                      “{ref.primarySubjectDesc}”
                                    </Text>
                                  )}
                                </Box>
                              </WrapItem>
                            );
                          })}
                        </Wrap>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* Titling */}
              {data.titling && (
                <>
                  <Divider />
                  <Box>
                    <HStack mb={2} spacing={2}>
                      <Text fontSize="12px" fontWeight="700">Titling</Text>
                      {data.titling.engine && <Badge colorScheme="purple">{data.titling.engine}</Badge>}
                      {data.titling.format && <Badge variant="outline">{data.titling.format}</Badge>}
                      {data.titling.reconstructed && <Badge colorScheme="yellow" variant="subtle">reconstructed</Badge>}
                    </HStack>
                    {data.titling.error ? (
                      <Text fontSize="12px" color="red.500">{data.titling.error}</Text>
                    ) : metaRows.length ? (
                      <VStack align="stretch" spacing={1}>
                        {metaRows.map(([k, v]) => (
                          <HStack key={k} fontSize="12px" align="start">
                            <Text minW="130px" color="brand.muted" fontFamily="mono">{k}</Text>
                            <Text>{String(v)}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="12px" color="brand.muted">No titling elements resolved.</Text>
                    )}
                  </Box>
                </>
              )}

              {/* Static image */}
              {data.image && (
                <>
                  <Divider />
                  <Box>
                    <HStack mb={2} spacing={2} justify="space-between">
                      <HStack spacing={2}>
                        <Text fontSize="12px" fontWeight="700">Static image generation</Text>
                        {data.image.model && <Badge title="The layout LLM that authored the HTML — not the image model">layout: {data.image.model}</Badge>}
                      </HStack>
                      {data.image.aiCanvasArtifactId && !editingPrompt && (
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={startEditingPrompt}
                          isDisabled={isExported || isRegenerating}
                          title={
                            isExported     ? 'Exported ads are read-only'
                          : isRegenerating ? 'Already regenerating'
                          : 'Edit the exact prompt and regenerate this ad from it'
                          }
                        >
                          Edit prompt &amp; regenerate
                        </Button>
                      )}
                    </HStack>

                    {/* WHICH PIPELINE made this ad — the single most-asked
                        debugging question, answered without a code-reading
                        session. Rendered whenever we know the answer, even if
                        imageGeneration itself is absent (HTML fallback). */}
                    {data.image.pipeline && (
                      <HStack mb={2} spacing={2}>
                        <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                          Pipeline
                        </Text>
                        <Badge colorScheme={data.image.pipeline === 'direct_image' ? 'green' : 'gray'} variant="solid" fontSize="0.65rem">
                          {data.image.pipeline === 'direct_image' ? 'Direct image' : data.image.pipeline}
                        </Badge>
                        {data.image.pipeline !== 'direct_image' && (
                          <Text fontSize="10px" color="brand.muted">
                            legacy/fallback path — not the current default
                          </Text>
                        )}
                      </HStack>
                    )}

                    {/* Which intent ran, whether it fell back, what the density
                        budget sacrificed. Absent on ads that predate this field
                        or on the legacy HTML/overlay paths, which never
                        resolved an intent. */}
                    {data.image.intentResolution && (
                      <Box mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
                        <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase" mb={2}>
                          Intent resolution
                        </Text>
                        <HStack spacing={2} flexWrap="wrap" mb={data.image.intentResolution.droppedRoles?.length ? 2 : 0}>
                          <Badge colorScheme="blue" variant="subtle" fontSize="0.6rem">
                            {data.image.intentResolution.delivered}
                          </Badge>
                          {data.image.intentResolution.fellBackFrom && (
                            <Badge colorScheme="orange" variant="subtle" fontSize="0.6rem" title="Requested intent's data requirement wasn't met, so the resolver walked down the hierarchy — working as designed, not a fault.">
                              fell back from {data.image.intentResolution.fellBackFrom}
                            </Badge>
                          )}
                          {data.image.intentResolution.generateSize && (
                            <Badge variant="outline" fontSize="0.6rem">gen {data.image.intentResolution.generateSize}</Badge>
                          )}
                          <Badge colorScheme={data.image.intentResolution.logoComposited ? 'green' : 'gray'} variant="outline" fontSize="0.6rem">
                            logo {data.image.intentResolution.logoComposited ? 'composited' : 'none'}
                          </Badge>
                        </HStack>
                        {!!data.image.intentResolution.droppedRoles?.length && (
                          <Text fontSize="10px" color="brand.muted">
                            sacrificed to density budget: {data.image.intentResolution.droppedRoles.join(', ')}
                          </Text>
                        )}
                      </Box>
                    )}

                    {/* Per-stage wall time — "why is THIS ad slow" without a
                        log search. direct_image's renderMs is the Atlas
                        submit+poll round trip, normally 60-150s. */}
                    {data.image.renderStages && (
                      <HStack mb={3} spacing={3} fontSize="10px" color="brand.muted">
                        <Text fontWeight="700" textTransform="uppercase">Timing</Text>
                        {data.image.renderStages.deriveMs != null && <Text>derive {(data.image.renderStages.deriveMs / 1000).toFixed(1)}s</Text>}
                        {data.image.renderStages.renderMs != null && <Text>render {(data.image.renderStages.renderMs / 1000).toFixed(1)}s</Text>}
                        {data.image.renderStages.uploadMs != null && <Text>upload {(data.image.renderStages.uploadMs / 1000).toFixed(1)}s</Text>}
                      </HStack>
                    )}

                    {/* What the IMAGE model actually received — recorded from the
                        request body at submit time, never re-derived at view time. */}
                    {data.image.imageGeneration && (
                      <Box mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
                        <HStack mb={2} spacing={2} flexWrap="wrap">
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                            Sent to the image model
                          </Text>
                          {/* A receipt-only record has no imageCount; without this
                              guard the badge read "undefined images". Say what
                              actually happened instead — the submit was charged
                              and returned nothing. */}
                          {data.image.imageGeneration.receiptOnly ? (
                            <Badge colorScheme="orange" variant="subtle" fontSize="0.6rem"
                              title="The image submit was charged but never returned an image. Only the receipt was stored.">
                              receipt only — no image returned
                            </Badge>
                          ) : (
                            <Badge colorScheme="green" variant="subtle" fontSize="0.6rem">
                              {data.image.imageGeneration.imageCount} image{data.image.imageGeneration.imageCount === 1 ? '' : 's'}
                            </Badge>
                          )}
                          <Badge colorScheme="purple" variant="subtle" fontSize="0.6rem">
                            {data.image.imageGeneration.model}
                          </Badge>
                          {data.image.imageGeneration.provider !== 'atlas' && (
                            <Badge colorScheme="orange" variant="subtle" fontSize="0.6rem">
                              {data.image.imageGeneration.provider}
                            </Badge>
                          )}
                          {data.image.imageGeneration.size && (
                            <Badge variant="outline" fontSize="0.6rem">{data.image.imageGeneration.size}</Badge>
                          )}
                          {data.image.imageGeneration.quality && (
                            <Badge variant="outline" fontSize="0.6rem">q:{data.image.imageGeneration.quality}</Badge>
                          )}
                          {/* The prompt is right below, read-only, and until now
                              there was no way to act on what you were reading —
                              the only edit button on this modal drives the LAYOUT
                              LLM prompt and is gated on aiCanvasArtifactId, which
                              the live direct_image path never stamps. */}
                          {/* data.kind mirrors the server's own gate: imagePromptRaw
                              is 400'd for anything but an image ad, and this block
                              can render for a non-image ad that merely carries an
                              imageGeneration record (routes/ads.js:2796). */}
                          {!editingImagePrompt && data.kind === 'image' && data.image.imageGeneration.prompt && (
                            <Button
                              size="xs"
                              variant="outline"
                              ml="auto"
                              onClick={startEditingImagePrompt}
                              isDisabled={isExported || isRegenerating}
                              title={
                                isExported     ? 'Exported ads are read-only'
                              : isRegenerating ? 'Already regenerating'
                              : 'Edit this exact prompt and regenerate the ad from it'
                              }
                            >
                              Edit &amp; regenerate
                            </Button>
                          )}
                        </HStack>

                        {/* CRASH SITE, FIXED 2026-08-25. This was
                            `imageGeneration.images.length > 0` — a bare access
                            inside a guard that only checks `imageGeneration`
                            itself. A RECEIPT-ONLY record passes that guard and
                            has no `images`, so `.length` threw
                            "Cannot read properties of undefined (reading
                            'length')". There is no error boundary above this
                            modal, so the whole app went to a blank white
                            screen and needed a manual reload — triggered by
                            the entirely reasonable act of clicking
                            "Generation details" on a failed ad.
                            Verified against the live staging payload for ad
                            6a8d6e34f0827b0ad1fb1ab1 (Atlas quota failure):
                            imageGeneration = {predictionId, model,
                            submittedAt, receiptOnly:true}. */}
                        {!!data.image.imageGeneration.images?.length && (
                          <Wrap spacing={2} mb={2}>
                            {data.image.imageGeneration.images!.map((img) => {
                              const original = img.originalUrl || img.sourceUrl || null;
                              const sent = img.submittedUrl || original;
                              const src = original || sent;
                              const processed = !!(original && sent && original !== sent);
                              return (
                                <WrapItem key={img.position}>
                                  {src ? (
                                    <ClickableSeedThumb
                                      src={src}
                                      alt={img.role || `ref ${img.position}`}
                                      badge={img.role || (img.position === 0 ? 'seed' : `#${img.position}`)}
                                      processed={processed}
                                      onOpen={() => setCompare({
                                        sentUrl: sent || src,
                                        originalUrl: original,
                                        label: img.role || `ref ${img.position}`,
                                        method: processed ? 'submitted' : 'unprocessed',
                                        processed
                                      })}
                                    />
                                  ) : (
                                    <Box boxSize="88px" borderRadius="md" borderWidth="1px" borderColor="brand.border"
                                      display="flex" alignItems="center" justifyContent="center">
                                      <Text fontSize="10px" color="brand.muted">no URL</Text>
                                    </Box>
                                  )}
                                </WrapItem>
                              );
                            })}
                          </Wrap>
                        )}

                        {editingImagePrompt ? (
                          <VStack align="stretch" spacing={2}>
                            <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                              Prompt sent to the image model — editing
                            </Text>
                            <Text fontSize="xs" color="orange.600" fontWeight="600">
                              This REPLACES the whole prompt. Product-fidelity, the exact-copy contract,
                              the no-invented-logo rules and the safe-box geometry are dropped unless you
                              keep them in the text. Size, crop, reference images and logo placement are
                              unaffected. Regenerating costs one image generation.
                            </Text>
                            <Textarea
                              value={editImageRaw}
                              onChange={e => setEditImageRaw(e.target.value)}
                              fontFamily="mono" fontSize="12px" minH="320px" maxH="520px"
                              maxLength={40000}
                            />
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="brand.muted">{editImageRaw.length}/40000</Text>
                              <HStack spacing={2}>
                                <Button size="sm" variant="ghost"
                                  onClick={() => setEditingImagePrompt(false)} isDisabled={regenSubmitting}>
                                  Cancel
                                </Button>
                                <Button
                                  size="sm" variant="brand"
                                  onClick={() => void submitImagePromptRegenerate()}
                                  isLoading={regenSubmitting}
                                  isDisabled={!editImageRaw.trim()}
                                >
                                  Regenerate with this prompt
                                </Button>
                              </HStack>
                            </HStack>
                          </VStack>
                        ) : (
                          <>
                          <PromptBlock
                            label="Exact prompt sent to the image model"
                            text={data.image.imageGeneration.prompt}
                            emptyHint={data.image.imageGeneration.receiptOnly
                              ? 'Receipt only — the submit was charged but never returned an image, so the prompt was not stored on this record.'
                              : 'No image-model prompt is stored for this ad.'}
                          />
                          {/* Same submitted-parameter treatment as video, so
                              "everything the model received" means the same
                              thing on both paths. */}
                          {data.image.submission && (
                            <>
                              <Box h={3} />
                              <SubmissionBlock label="Submitted to the image model" submission={data.image.submission} />
                              {(data.image.submission.operatorInputs?.lastRefinementPrompt
                                || data.image.submission.operatorInputs?.lastRefinementWasRawEdit) && (
                                <PromptBlock
                                  label="Last regenerate — operator refinement note"
                                  text={data.image.submission.operatorInputs?.lastRefinementPrompt}
                                  emptyHint={data.image.submission.operatorInputs?.lastRefinementWasRawEdit
                                    ? 'The last regenerate replaced the whole image prompt verbatim. That text is not stored on the ad (image overrides are pass-through), so only the fact that one ran is recorded.'
                                    : undefined}
                                />
                              )}
                            </>
                          )}
                          </>
                        )}
                      </Box>
                    )}

                    {data.image.fontResolution && (
                      <Box mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
                        <HStack mb={2} spacing={2}>
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase">
                            Resolved typography
                          </Text>
                          <Badge colorScheme={data.image.fontResolution.heading?.exact ? 'green' : 'yellow'} variant="subtle">
                            {data.image.fontResolution.heading?.exact ? 'exact' : 'closest match'}
                          </Badge>
                        </HStack>
                        <VStack align="stretch" spacing={1}>
                          {(['heading', 'body'] as const).map((role) => {
                            const font = data.image?.fontResolution?.[role];
                            if (!font) return null;
                            const changed = font.requestedFamily && font.resolvedFamily && font.requestedFamily !== font.resolvedFamily;
                            return (
                              <HStack key={role} fontSize="12px" align="start">
                                <Text minW="68px" color="brand.muted" textTransform="capitalize">{role}</Text>
                                <Text>
                                  {font.requestedFamily || 'Unknown'}
                                  {changed ? ` → ${font.resolvedFamily}` : ''}
                                  {font.source ? ` · ${font.source}` : ''}
                                </Text>
                              </HStack>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}

                    {isRegenerating && (
                      <HStack mb={3} p={2} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface" spacing={2}>
                        <Spinner size="sm" />
                        <Text fontSize="12px" fontWeight="700">{imageStageLabel(data.regenerationStage)}</Text>
                      </HStack>
                    )}
                    {regenError && !isRegenerating && (
                      <Box mb={3} p={2} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md">
                        <Text fontSize="12px" color="red.700">{regenError}</Text>
                      </Box>
                    )}

                    {editingPrompt ? (
                      <VStack align="stretch" spacing={2}>
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1} textTransform="uppercase">
                            Layout prompt (system)
                          </Text>
                          <Textarea value={editSystem} onChange={e => setEditSystem(e.target.value)}
                            fontFamily="mono" fontSize="12px" minH="160px" maxH="320px" />
                        </Box>
                        <Box>
                          <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1} textTransform="uppercase">
                            Layout prompt (user)
                          </Text>
                          <Textarea value={editUser} onChange={e => setEditUser(e.target.value)}
                            fontFamily="mono" fontSize="12px" minH="160px" maxH="320px" />
                        </Box>
                        <HStack justify="flex-end" spacing={2}>
                          <Button size="sm" variant="ghost" onClick={() => setEditingPrompt(false)} isDisabled={regenSubmitting}>
                            Cancel
                          </Button>
                          <Button
                            size="sm" variant="brand"
                            onClick={() => void submitPromptRegenerate()}
                            isLoading={regenSubmitting}
                            isDisabled={!editSystem.trim() || !editUser.trim()}
                          >
                            Regenerate with this prompt
                          </Button>
                        </HStack>
                      </VStack>
                    ) : (
                      <>
                        <PromptBlock label="Layout LLM prompt (system) — not the image model" text={data.image.layoutPrompt?.system} />
                        <Box h={2} />
                        <PromptBlock label="Layout LLM prompt (user) — not the image model" text={data.image.layoutPrompt?.user} />
                      </>
                    )}
                  </Box>
                </>
              )}
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    {compare && <SeedCompareModal target={compare} onClose={() => setCompare(null)} />}
    </>
  );
}
